import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";

const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 38,
    paddingHorizontal: 34,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: "#111827",
  },
  titulo: {
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 5,
  },
  descripcion: {
    fontSize: 9,
    textAlign: "center",
    color: "#4b5563",
    marginBottom: 14,
  },
  datos: {
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 4,
    padding: 8,
    marginBottom: 12,
  },
  filaDatos: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 3,
  },
  dato: {
    width: "50%",
    paddingRight: 8,
  },
  datoLabel: {
    fontWeight: "bold",
  },
  seccion: {
    marginBottom: 12,
  },
  seccionTitulo: {
    fontSize: 12,
    fontWeight: "bold",
    backgroundColor: "#e5e7eb",
    paddingVertical: 5,
    paddingHorizontal: 7,
    marginBottom: 7,
  },
  campo: {
    marginBottom: 9,
    paddingBottom: 7,
    borderBottomWidth: 0.6,
    borderBottomColor: "#d1d5db",
  },
  campoLabel: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 3,
  },
  requerido: {
    color: "#b91c1c",
  },
  ayuda: {
    fontSize: 7.5,
    color: "#6b7280",
    marginTop: 3,
  },
  cajaRespuesta: {
    height: 22,
    borderWidth: 1,
    borderColor: "#9ca3af",
    borderRadius: 3,
  },
  cajaTextarea: {
    height: 58,
    borderWidth: 1,
    borderColor: "#9ca3af",
    borderRadius: 3,
  },
  opcion: {
    marginBottom: 3,
  },
  html: {
    padding: 6,
    backgroundColor: "#f3f4f6",
    borderRadius: 3,
  },
  repetible: {
    borderWidth: 1,
    borderColor: "#9ca3af",
    borderRadius: 3,
    padding: 6,
    minHeight: 42,
  },
  pie: {
    position: "absolute",
    bottom: 18,
    left: 34,
    right: 34,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 7,
    color: "#6b7280",
  },
});

const limpiarHtml = (html = "") =>
  String(html)
    .replace(/<br\s*\/?\s*>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .trim();

const textoSiNo = (valor) => (valor ? "Sí" : "No");

const nombreArchivoSeguro = (nombre = "formulario") =>
  String(nombre || "formulario")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase() || "formulario";

const VistaCampoPdf = ({ campo, indice }) => {
  const opciones = campo.opciones || [];
  const esRequerido = campo.requerido || campo.super_requerido;

  return (
    <View style={pdfStyles.campo}>
      {campo.tipo === "html_static" ? (
        <>
          <Text style={pdfStyles.campoLabel}>
            {indice}. {campo.label || "Texto informativo"}
          </Text>
          <Text style={pdfStyles.html}>
            {limpiarHtml(campo.contenido_html) || "Sin contenido HTML"}
          </Text>
        </>
      ) : (
        <>
          <Text style={pdfStyles.campoLabel}>
            {indice}. {campo.label || "Campo sin nombre"}
            {esRequerido ? <Text style={pdfStyles.requerido}> *</Text> : null}
          </Text>

          {(campo.tipo === "text" ||
            campo.tipo === "number" ||
            campo.tipo === "date" ||
            campo.tipo === "direccion") && (
            <View style={pdfStyles.cajaRespuesta} />
          )}

          {campo.tipo === "textarea" && (
            <View style={pdfStyles.cajaTextarea} />
          )}

          {(campo.tipo === "select" ||
            campo.tipo === "multi-select" ||
            campo.tipo === "aceptacion_obligatoria") && (
            <View>
              {opciones.length > 0 ? (
                opciones.map((op, opIndex) => (
                  <Text key={`${op.valor || op.label || opIndex}_${opIndex}`} style={pdfStyles.opcion}>
                    {campo.tipo === "multi-select" ? "[ ]" : "( )"} {op.label || op.valor || `Opción ${opIndex + 1}`}
                  </Text>
                ))
              ) : (
                <Text style={pdfStyles.ayuda}>Sin opciones configuradas</Text>
              )}
            </View>
          )}

          {campo.tipo === "boolean" && (
            <View>
              <Text style={pdfStyles.opcion}>( ) {campo.opciones_boolean?.si || "Sí"}</Text>
              <Text style={pdfStyles.opcion}>( ) {campo.opciones_boolean?.no || "No"}</Text>
            </View>
          )}

          {campo.tipo === "repetible" && (
            <View style={pdfStyles.repetible}>
              <Text>Espacio para registros repetibles</Text>
              <Text style={pdfStyles.ayuda}>
                Modelo: {campo.modelo || "Sin modelo"}
                {campo.tipo_relacion ? ` · Relación: ${campo.tipo_relacion}` : ""}
              </Text>
            </View>
          )}

          <Text style={pdfStyles.ayuda}>
            Tipo: {campo.tipo || "text"} · Clave: {campo.clave || "Sin clave"} · Origen: {campo.origen || "formulario"}
            {campo.origen === "persona" ? ` · Campo DB: ${campo.campo_db || "sin definir"} · Herencia: ${campo.herencia || "readonly"}` : ""}
            {` · Visible: ${textoSiNo(campo.visible ?? true)} · Imprimible: ${textoSiNo(campo.imprimible ?? true)}`}
            {campo.valor_obligatorio ? ` · Valor obligatorio: ${campo.valor_obligatorio}` : ""}
          </Text>
        </>
      )}
    </View>
  );
};

const FormularioPdfDocument = ({ formulario, secciones }) => (
  <Document
    title={formulario.nombre || "Vista previa del formulario"}
    author="ISeP Santa Fe"
    subject="Vista previa del formulario"
  >
    <Page size="A4" style={pdfStyles.page}>
      <Text style={pdfStyles.titulo}>
        {formulario.nombre || "Formulario sin nombre"}
      </Text>

      {formulario.descripcion ? (
        <Text style={pdfStyles.descripcion}>{formulario.descripcion}</Text>
      ) : null}

      <View style={pdfStyles.datos}>
        <View style={pdfStyles.filaDatos}>
          <Text style={pdfStyles.dato}>
            <Text style={pdfStyles.datoLabel}>Escuela: </Text>
            {formulario.escuela || "Sin escuela"}
          </Text>
          <Text style={pdfStyles.dato}>
            <Text style={pdfStyles.datoLabel}>Sede: </Text>
            {formulario.sede || "Sin sede"}
          </Text>
        </View>
        <View style={pdfStyles.filaDatos}>
          <Text style={pdfStyles.dato}>
            <Text style={pdfStyles.datoLabel}>Tipo: </Text>
            {formulario.tipo || "Sin tipo"}
          </Text>
          <Text style={pdfStyles.dato}>
            <Text style={pdfStyles.datoLabel}>Impresión: </Text>
            {formulario.impresion || "Sin impresión"}
          </Text>
        </View>
        <View style={pdfStyles.filaDatos}>
          <Text style={pdfStyles.dato}>
            <Text style={pdfStyles.datoLabel}>Apertura: </Text>
            {formulario.fechaInicio || "Sin fecha"}
          </Text>
          <Text style={pdfStyles.dato}>
            <Text style={pdfStyles.datoLabel}>Cierre: </Text>
            {formulario.fechaFinalizacion || "Sin fecha"}
          </Text>
        </View>
        <View style={pdfStyles.filaDatos}>
          <Text style={pdfStyles.dato}>
            <Text style={pdfStyles.datoLabel}>Restricción: </Text>
            {formulario.restricciones || "Sin restricción"}
          </Text>
          <Text style={pdfStyles.dato}>
            <Text style={pdfStyles.datoLabel}>Turnos: </Text>
            {formulario.turnos || "Sin turnos"}
          </Text>
        </View>
      </View>

      {(secciones || []).length > 0 ? (
        secciones.map((sec, seccionIndex) => (
          <View key={sec._uid || sec.id || seccionIndex} style={pdfStyles.seccion}>
            <Text style={pdfStyles.seccionTitulo}>
              Sección {seccionIndex + 1}: {sec.nombre || "Sin nombre"}
            </Text>
            {(sec.campos || []).length > 0 ? (
              (sec.campos || []).map((campo, campoIndex) => (
                <VistaCampoPdf
                  key={campo._uid || campo.id || campo.clave || campoIndex}
                  campo={campo}
                  indice={`${seccionIndex + 1}.${campoIndex + 1}`}
                />
              ))
            ) : (
              <Text style={pdfStyles.ayuda}>Esta sección no tiene campos.</Text>
            )}
          </View>
        ))
      ) : (
        <Text>No hay secciones cargadas.</Text>
      )}

      <View style={pdfStyles.pie} fixed>
        <Text>Vista previa generada desde Form Builder PRO</Text>
        <Text render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />
      </View>
    </Page>
  </Document>
);

const generarIdTemp = () => `tmp_${Date.now()}_${Math.random()}`;

const normalizarFechaInput = (fecha) => {
  if (!fecha) return "";

  if (
    typeof fecha === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(fecha)
  ) {
    return fecha;
  }

  if (typeof fecha === "string" && fecha.includes("T")) {
    return fecha.split("T")[0];
  }

  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return "";

  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${y}-${m}-${day}`;
};

const normalizarBool = (valor) =>
  valor === true ||
  valor === 1 ||
  valor === "1" ||
  valor === "true";

const normalizarHerencia = (campo = {}) => {
  if (campo.origen !== "persona") return "readonly";
  return campo.herencia === "editable" ? "editable" : "readonly";
};

const clonarSeguro = (obj) => {
  if (typeof structuredClone === "function") {
    return structuredClone(obj);
  }

  return JSON.parse(JSON.stringify(obj));
};

const crearCampoClonado = (campo) => {
  const copia = clonarSeguro(campo);

  copia.id = generarIdTemp();
  copia._uid = generarIdTemp();
  copia.clave = `${campo.clave || "campo"}_copia_${Date.now()}`;
  copia.depende_de = null;
  copia.imprimible = true;

  return copia;
};

const reordenarCampos = (campos = []) =>
  campos.map((campo, orden) => ({
    ...campo,
    orden,
  }));

const escuelas = [
  "",
  "Escuela de Policía",
  "Escuela de Policia",
  "Escuela de Investigaciones",
  "Escuela de Especialidades",
  "Especialidades",
  "Superior",
];

const sedes = [
  "",
  "Rosario",
  "Recreo",
  "Reconquista",
  "Rosario y Recreo",
  "Rosario, Recreo y Reconquista",
];

const tipos = ["Cuestionario", "Inscripcion", "Formulario"];

const restricciones = [
  "",
  "Inscripciones",
  "Perfeccionamiento",
  "Policial",
];

const impresiones = [
  "",
  "Declaracion Jurada",
  "Medico",
  "Psicologia",
  "Socioambiental",
];

const styles = {
  page: {
    padding: 20,
    background: "#f3f4f6",
    minHeight: "100vh",
    fontFamily: "Arial, sans-serif",
  },
  title: {
    marginBottom: 16,
    fontSize: 28,
    fontWeight: 800,
  },
  card: {
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#374151",
  },
  input: {
    padding: "9px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    fontSize: 14,
    width: "100%",
    boxSizing: "border-box",
  },
  textarea: {
    padding: "9px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    fontSize: 14,
    minHeight: 80,
    width: "100%",
    boxSizing: "border-box",
  },
  button: {
    padding: "8px 12px",
    border: "none",
    borderRadius: 8,
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 700,
    marginRight: 6,
    marginTop: 6,
  },
  danger: {
    background: "#dc2626",
  },
  secondary: {
    background: "#64748b",
  },
  success: {
    background: "#16a34a",
  },
  preview: {
    background: "#7c3aed",
  },
  warningBox: {
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    borderRadius: 8,
    padding: 10,
    marginTop: 10,
    fontSize: 13,
  },
  sectionHeader: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  campoCard: {
    background: "#f8fafc",
    border: "1px dashed #94a3b8",
    borderRadius: 10,
    padding: 12,
    marginTop: 12,
  },
  checks: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    marginTop: 10,
  },
  bulkBar: {
    background: "#111827",
    color: "#fff",
    borderRadius: 12,
    padding: 12,
    marginTop: 12,
    marginBottom: 16,
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  selectedCampoCard: {
    border: "2px solid #2563eb",
    background: "#eff6ff",
  },
};

const Input = memo(({ label, children }) => (
  <div style={styles.field}>
    <label style={styles.label}>{label}</label>
    {children}
  </div>
));

Input.displayName = "Input";

const DatosFormulario = memo(
  ({ formulario, actualizarFormulario }) => {
    return (
      <div style={styles.card}>
        <h3>Datos del formulario</h3>

        <div style={styles.grid}>
          <Input label="Nombre">
            <input
              style={styles.input}
              value={formulario.nombre}
              onChange={(e) =>
                actualizarFormulario("nombre", e.target.value)
              }
            />
          </Input>

          <Input label="Descripción">
            <input
              style={styles.input}
              value={formulario.descripcion}
              onChange={(e) =>
                actualizarFormulario("descripcion", e.target.value)
              }
            />
          </Input>

          <Input label="Escuela">
            <select
              style={styles.input}
              value={formulario.escuela}
              onChange={(e) =>
                actualizarFormulario("escuela", e.target.value)
              }
            >
              {escuelas.map((op) => (
                <option key={op} value={op}>
                  {op || "Sin escuela"}
                </option>
              ))}
            </select>
          </Input>

          <Input label="Sede">
            <select
              style={styles.input}
              value={formulario.sede}
              onChange={(e) =>
                actualizarFormulario("sede", e.target.value)
              }
            >
              {sedes.map((op) => (
                <option key={op} value={op}>
                  {op || "Sin sede"}
                </option>
              ))}
            </select>
          </Input>

          <Input label="Fecha apertura">
            <input
              style={styles.input}
              type="date"
              value={formulario.fechaInicio}
              onChange={(e) =>
                actualizarFormulario("fechaInicio", e.target.value)
              }
            />
          </Input>

          <Input label="Fecha cierre">
            <input
              style={styles.input}
              type="date"
              value={formulario.fechaFinalizacion}
              onChange={(e) =>
                actualizarFormulario(
                  "fechaFinalizacion",
                  e.target.value
                )
              }
            />
          </Input>

          <Input label="Tipo">
            <select
              style={styles.input}
              value={formulario.tipo}
              onChange={(e) =>
                actualizarFormulario("tipo", e.target.value)
              }
            >
              {tipos.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
          </Input>

          <Input label="Restricciones">
            <select
              style={styles.input}
              value={formulario.restricciones}
              onChange={(e) =>
                actualizarFormulario("restricciones", e.target.value)
              }
            >
              {restricciones.map((op) => (
                <option key={op} value={op}>
                  {op || "Sin restricción"}
                </option>
              ))}
            </select>
          </Input>

          <Input label="Impresión">
            <select
              style={styles.input}
              value={formulario.impresion}
              onChange={(e) =>
                actualizarFormulario("impresion", e.target.value)
              }
            >
              {impresiones.map((op) => (
                <option key={op} value={op}>
                  {op || "Sin impresión"}
                </option>
              ))}
            </select>
          </Input>

          <Input label="Turnos">
            <input
              style={styles.input}
              value={formulario.turnos}
              onChange={(e) =>
                actualizarFormulario("turnos", e.target.value)
              }
              placeholder="Ej: auxiliar_forense_turnos_2026"
            />
          </Input>
        </div>

        <div style={styles.checks}>
          <label>
            <input
              type="checkbox"
              checked={formulario.activo}
              onChange={(e) =>
                actualizarFormulario("activo", e.target.checked)
              }
            />{" "}
            Activo
          </label>

          <label>
            <input
              type="checkbox"
              checked={formulario.restringeFormulario}
              onChange={(e) =>
                actualizarFormulario(
                  "restringeFormulario",
                  e.target.checked
                )
              }
            />{" "}
            Restringe formulario
          </label>
        </div>

        {formulario.restringeFormulario && (
          <div style={styles.warningBox}>
            Este formulario sólo será visible para usuarios que tengan en{" "}
            <b>datosTiempoMinimo.formularioHabilitado</b> el ID de este
            formulario.
          </div>
        )}
      </div>
    );
  }
);

DatosFormulario.displayName = "DatosFormulario";

const OpcionesEditor = memo(
  ({ campo, seccionIndex, campoIndex, agregarOpcion, eliminarOpcion, actualizarOpcion }) => {
    if (campo.tipo !== "select" && campo.tipo !== "multi-select") {
      return null;
    }

    return (
      <div style={styles.warningBox}>
        <button
          type="button"
          style={styles.button}
          onClick={() => agregarOpcion(seccionIndex, campoIndex)}
        >
          + opción
        </button>

        {(campo.opciones || []).map((op, opcionIndex) => (
          <div
            key={op.id || opcionIndex}
            style={{
              display: "grid",
              gridTemplateColumns:
                "40px minmax(120px, 1fr) minmax(120px, 1fr)",
              gap: 8,
              marginTop: 8,
            }}
          >
            <button
              type="button"
              style={{
                ...styles.button,
                ...styles.danger,
                margin: 0,
              }}
              onClick={() =>
                eliminarOpcion(seccionIndex, campoIndex, opcionIndex)
              }
            >
              ❌
            </button>

            <input
              style={styles.input}
              placeholder="Label"
              value={op.label || ""}
              onChange={(e) =>
                actualizarOpcion(
                  seccionIndex,
                  campoIndex,
                  opcionIndex,
                  "label",
                  e.target.value
                )
              }
            />

            <input
              style={styles.input}
              placeholder="Valor"
              value={op.valor || ""}
              onChange={(e) =>
                actualizarOpcion(
                  seccionIndex,
                  campoIndex,
                  opcionIndex,
                  "valor",
                  e.target.value
                )
              }
            />
          </div>
        ))}
      </div>
    );
  }
);

OpcionesEditor.displayName = "OpcionesEditor";

const BooleanEditor = memo(
  ({ campo, seccionIndex, campoIndex, actualizarCampoInterno }) => {
    if (campo.tipo !== "boolean") return null;

    return (
      <div style={styles.warningBox}>
        <div style={styles.grid}>
          <Input label='Texto "No"'>
            <input
              style={styles.input}
              value={campo.opciones_boolean?.no || ""}
              onChange={(e) =>
                actualizarCampoInterno(seccionIndex, campoIndex, {
                  opciones_boolean: {
                    ...(campo.opciones_boolean || {}),
                    no: e.target.value,
                  },
                })
              }
            />
          </Input>

          <Input label='Texto "Sí"'>
            <input
              style={styles.input}
              value={campo.opciones_boolean?.si || ""}
              onChange={(e) =>
                actualizarCampoInterno(seccionIndex, campoIndex, {
                  opciones_boolean: {
                    ...(campo.opciones_boolean || {}),
                    si: e.target.value,
                  },
                })
              }
            />
          </Input>
        </div>
      </div>
    );
  }
);

BooleanEditor.displayName = "BooleanEditor";

const CampoPersonaEditor = memo(
  ({ campo, seccionIndex, campoIndex, actualizarCampo }) => {
    if (campo.origen !== "persona") return null;

    return (
      <div style={styles.warningBox}>
        <div style={styles.grid}>
          <Input label="Campo DB Persona">
            <input
              style={styles.input}
              value={campo.campo_db || ""}
              placeholder="Ej: dni, nombre, apellido, email, datosTiempoMinimo.jerarquia"
              onChange={(e) =>
                actualizarCampo(
                  seccionIndex,
                  campoIndex,
                  "campo_db",
                  e.target.value
                )
              }
            />
          </Input>

          <Input label="Herencia">
            <select
              style={styles.input}
              value={campo.herencia || "readonly"}
              onChange={(e) =>
                actualizarCampo(
                  seccionIndex,
                  campoIndex,
                  "herencia",
                  e.target.value
                )
              }
            >
              <option value="readonly">Solo lectura</option>
              <option value="editable">Precarga y permite editar</option>
            </select>
          </Input>
        </div>
      </div>
    );
  }
);

CampoPersonaEditor.displayName = "CampoPersonaEditor";

const HtmlEditor = memo(({ campo, seccionIndex, campoIndex, actualizarCampo }) => {
  if (campo.tipo !== "html_static") return null;

  return (
    <Input label="HTML">
      <textarea
        style={styles.textarea}
        value={campo.contenido_html || ""}
        onChange={(e) =>
          actualizarCampo(
            seccionIndex,
            campoIndex,
            "contenido_html",
            e.target.value
          )
        }
      />
    </Input>
  );
});

HtmlEditor.displayName = "HtmlEditor";

const RepetibleEditor = memo(({ campo, seccionIndex, campoIndex, actualizarCampo }) => {
  if (campo.tipo !== "repetible") return null;

  return (
    <div style={styles.warningBox}>
      <Input label="Modelo repetible">
        <select
          style={styles.input}
          value={campo.modelo || ""}
          onChange={(e) =>
            actualizarCampo(seccionIndex, campoIndex, "modelo", e.target.value)
          }
        >
          <option value="">Seleccionar modelo</option>
          <option value="persona_hijos">Familiares</option>
          <option value="persona_experiencia">Experiencia</option>
        </select>
      </Input>

      {campo.modelo === "persona_hijos" && (
        <Input label="Tipo relación">
          <select
            style={styles.input}
            value={campo.tipo_relacion || "Otro"}
            onChange={(e) =>
              actualizarCampo(
                seccionIndex,
                campoIndex,
                "tipo_relacion",
                e.target.value
              )
            }
          >
            <option value="Hijo">Hijo</option>
            <option value="Hermano">Hermano</option>
            <option value="Pareja">Pareja</option>
            <option value="Otro">Otro</option>
          </select>
        </Input>
      )}
    </div>
  );
});

RepetibleEditor.displayName = "RepetibleEditor";

const DependenciaEditor = memo(
  ({
    campo,
    seccionIndex,
    campoIndex,
    camposDisponibles,
    actualizarCampoInterno,
  }) => {
    const campoPadre = useMemo(
      () =>
        (camposDisponibles || []).find(
          (item) => String(item.id) === String(campo.depende_de)
        ) || null,
      [camposDisponibles, campo.depende_de]
    );

    const valorDependencia = campo.valor_dependencia || {
      tipo: "igual",
      valores: [],
    };

    const tipoCondicion = valorDependencia.tipo || "igual";
    const valoresSeleccionados = Array.isArray(valorDependencia.valores)
      ? valorDependencia.valores.map(String)
      : [];

    const opcionesPadre = useMemo(() => {
      if (!campoPadre) return [];

      if (campoPadre.tipo === "boolean") {
        return [
          {
            label: campoPadre.opciones_boolean?.si || "Sí",
            valor: "true",
          },
          {
            label: campoPadre.opciones_boolean?.no || "No",
            valor: "false",
          },
        ];
      }

      if (campoPadre.tipo === "aceptacion_obligatoria") {
        return campoPadre.opciones?.length
          ? campoPadre.opciones
          : [
              { label: "Sí, acepto", valor: "SI" },
              { label: "No acepto", valor: "NO" },
            ];
      }

      if (
        campoPadre.tipo === "select" ||
        campoPadre.tipo === "multi-select"
      ) {
        return campoPadre.opciones || [];
      }

      return [];
    }, [campoPadre]);

    const actualizarDependencia = useCallback(
      (cambios) => {
        actualizarCampoInterno(seccionIndex, campoIndex, cambios);
      },
      [actualizarCampoInterno, seccionIndex, campoIndex]
    );

    const cambiarCampoPadre = useCallback(
      (valor) => {
        if (!valor) {
          actualizarDependencia({
            depende_de: null,
            valor_dependencia: {
              tipo: "igual",
              valores: [],
            },
          });
          return;
        }

        actualizarDependencia({
          depende_de: valor,
          valor_dependencia: {
            tipo: "igual",
            valores: [],
          },
        });
      },
      [actualizarDependencia]
    );

    const cambiarTipoCondicion = useCallback(
      (tipo) => {
        actualizarDependencia({
          valor_dependencia: {
            ...valorDependencia,
            tipo,
            valores:
              tipo === "vacio" || tipo === "no_vacio"
                ? []
                : valoresSeleccionados,
          },
        });
      },
      [actualizarDependencia, valorDependencia, valoresSeleccionados]
    );

    const cambiarValorSimple = useCallback(
      (valor) => {
        actualizarDependencia({
          valor_dependencia: {
            ...valorDependencia,
            valores: valor === "" ? [] : [valor],
          },
        });
      },
      [actualizarDependencia, valorDependencia]
    );

    const toggleValor = useCallback(
      (valor, checked) => {
        const valorString = String(valor);
        let nuevos = [...valoresSeleccionados];

        if (checked) {
          if (!nuevos.includes(valorString)) nuevos.push(valorString);
        } else {
          nuevos = nuevos.filter((item) => item !== valorString);
        }

        actualizarDependencia({
          valor_dependencia: {
            ...valorDependencia,
            valores: nuevos,
          },
        });
      },
      [actualizarDependencia, valorDependencia, valoresSeleccionados]
    );

    return (
      <div
        style={{
          ...styles.warningBox,
          background: "#eff6ff",
          border: "1px solid #93c5fd",
          color: "#1e3a8a",
          marginTop: 14,
        }}
      >
        <div
          style={{
            fontWeight: 800,
            marginBottom: 10,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          🔗 Visibilidad condicional
        </div>

        <div style={styles.grid}>
          <Input label="Depende de">
            <select
              style={styles.input}
              value={campo.depende_de || ""}
              onChange={(e) => cambiarCampoPadre(e.target.value)}
            >
              <option value="">No depende de otro campo</option>

              {(camposDisponibles || []).map((item) => (
                <option key={item._uid || item.id} value={item.id}>
                  {item.seccionNombre
                    ? `${item.seccionNombre} → `
                    : ""}
                  {item.label || item.clave || `Campo ${item.id}`}
                </option>
              ))}
            </select>
          </Input>

          {campo.depende_de && (
            <Input label="Condición">
              <select
                style={styles.input}
                value={tipoCondicion}
                onChange={(e) => cambiarTipoCondicion(e.target.value)}
              >
                <option value="igual">Igual a</option>
                <option value="distinto">Distinto de</option>
                <option value="incluye">Incluye alguno de</option>
                <option value="no_incluye">No incluye</option>
                <option value="vacio">Está vacío</option>
                <option value="no_vacio">No está vacío</option>
              </select>
            </Input>
          )}
        </div>

        {campo.depende_de &&
          tipoCondicion !== "vacio" &&
          tipoCondicion !== "no_vacio" && (
            <div style={{ marginTop: 10 }}>
              {opcionesPadre.length > 0 ? (
                tipoCondicion === "incluye" ||
                tipoCondicion === "no_incluye" ? (
                  <>
                    <div style={{ ...styles.label, marginBottom: 6 }}>
                      Valor/es que activan la condición
                    </div>

                    <div style={styles.checks}>
                      {opcionesPadre.map((op, index) => {
                        const valor = String(op.valor ?? "");
                        return (
                          <label
                            key={op.id || `${valor}_${index}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={valoresSeleccionados.includes(valor)}
                              onChange={(e) =>
                                toggleValor(valor, e.target.checked)
                              }
                            />
                            {op.label || op.valor}
                          </label>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <Input label="Valor que activa la condición">
                    <select
                      style={styles.input}
                      value={valoresSeleccionados[0] || ""}
                      onChange={(e) => cambiarValorSimple(e.target.value)}
                    >
                      <option value="">Seleccionar valor</option>
                      {opcionesPadre.map((op, index) => (
                        <option
                          key={op.id || `${op.valor}_${index}`}
                          value={String(op.valor ?? "")}
                        >
                          {op.label || op.valor}
                        </option>
                      ))}
                    </select>
                  </Input>
                )
              ) : (
                <Input label="Valor que activa la condición">
                  <input
                    style={styles.input}
                    value={valoresSeleccionados[0] || ""}
                    placeholder="Ej: SI, true, Rosario, etc."
                    onChange={(e) => cambiarValorSimple(e.target.value)}
                  />
                </Input>
              )}
            </div>
          )}

        {campo.depende_de && campoPadre && (
          <div style={{ marginTop: 10, fontSize: 12 }}>
            Este campo se mostrará según la respuesta de{" "}
            <b>{campoPadre.label || campoPadre.clave}</b>.
          </div>
        )}
      </div>
    );
  }
);

DependenciaEditor.displayName = "DependenciaEditor";

const CampoEditor = memo(
  ({
    campo,
    seccionIndex,
    campoIndex,
    totalCampos,
    actualizarCampo,
    actualizarCampoInterno,
    moverCampo,
    clonarCampo,
    eliminarCampo,
    agregarOpcion,
    eliminarOpcion,
    actualizarOpcion,
    seleccionado,
    toggleCampoSeleccionado,
    camposDisponibles,
  }) => {
    const tituloCampo = useMemo(
      () => campo.label || "Sin nombre",
      [campo.label]
    );

    const cambiarTipo = useCallback(
      (nuevoTipo) => {
        const cambios = { tipo: nuevoTipo };

        if (nuevoTipo === "aceptacion_obligatoria") {
          cambios.requerido = true;
          cambios.super_requerido = true;
          cambios.valor_obligatorio = "SI";
          cambios.opciones = [
            { label: "Sí, acepto", valor: "SI" },
            { label: "No acepto", valor: "NO" },
          ];
        }

        actualizarCampoInterno(seccionIndex, campoIndex, cambios);
      },
      [actualizarCampoInterno, seccionIndex, campoIndex]
    );

    const cambiarOrigen = useCallback(
      (nuevoOrigen) => {
        if (nuevoOrigen !== "persona") {
          actualizarCampoInterno(seccionIndex, campoIndex, {
            origen: nuevoOrigen,
            campo_db: "",
            herencia: "readonly",
          });
          return;
        }

        actualizarCampoInterno(seccionIndex, campoIndex, {
          origen: nuevoOrigen,
          herencia: campo.herencia || "readonly",
        });
      },
      [actualizarCampoInterno, seccionIndex, campoIndex, campo.herencia]
    );

    return (
      <div
        style={{
          ...styles.campoCard,
          ...(seleccionado ? styles.selectedCampoCard : {}),
        }}
      >
        <div style={styles.sectionHeader}>
          <h4 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={seleccionado}
              onChange={() => toggleCampoSeleccionado(campo._uid)}
            />
            Campo {campoIndex + 1}: {tituloCampo}
          </h4>

          <div>
            <button
              type="button"
              style={styles.button}
              disabled={campoIndex === 0}
              onClick={() => moverCampo(seccionIndex, campoIndex, "up")}
            >
              ⬆️
            </button>

            <button
              type="button"
              style={styles.button}
              disabled={campoIndex === totalCampos - 1}
              onClick={() => moverCampo(seccionIndex, campoIndex, "down")}
            >
              ⬇️
            </button>

            <button
              type="button"
              style={{ ...styles.button, ...styles.secondary }}
              onClick={() => clonarCampo(seccionIndex, campoIndex)}
            >
              📄
            </button>

            <button
              type="button"
              style={{ ...styles.button, ...styles.danger }}
              onClick={() => eliminarCampo(seccionIndex, campoIndex)}
            >
              ❌
            </button>
          </div>
        </div>

        <div style={styles.grid}>
          <Input label="Label">
            <input
              style={styles.input}
              value={campo.label || ""}
              onChange={(e) =>
                actualizarCampo(
                  seccionIndex,
                  campoIndex,
                  "label",
                  e.target.value
                )
              }
            />
          </Input>

          <Input label="Clave">
            <input
              style={styles.input}
              value={campo.clave || ""}
              onChange={(e) =>
                actualizarCampo(
                  seccionIndex,
                  campoIndex,
                  "clave",
                  e.target.value
                )
              }
            />
          </Input>

          <Input label="Tipo">
            <select
              style={styles.input}
              value={campo.tipo || "text"}
              onChange={(e) => cambiarTipo(e.target.value)}
            >
              <option value="text">Texto</option>
              <option value="textarea">Textarea</option>
              <option value="number">Número</option>
              <option value="boolean">Boolean</option>
              <option value="date">Fecha</option>
              <option value="select">Select</option>
              <option value="multi-select">Multi Select</option>
              <option value="aceptacion_obligatoria">
                ✅ Aceptación obligatoria
              </option>
              <option value="direccion">📍 Dirección / Mapa</option>
              <option value="repetible">Repetible</option>
              <option value="html_static">HTML</option>
            </select>
          </Input>

          <Input label="Origen">
            <select
              style={styles.input}
              value={campo.origen || "formulario"}
              onChange={(e) => cambiarOrigen(e.target.value)}
            >
              <option value="formulario">Formulario</option>
              <option value="persona">Persona DB</option>
            </select>
          </Input>

          <Input label="Valor obligatorio">
            <input
              style={styles.input}
              value={campo.valor_obligatorio || ""}
              onChange={(e) =>
                actualizarCampo(
                  seccionIndex,
                  campoIndex,
                  "valor_obligatorio",
                  e.target.value
                )
              }
            />
          </Input>
        </div>

        <div style={styles.checks}>
          <label>
            <input
              type="checkbox"
              checked={campo.requerido ?? false}
              onChange={(e) =>
                actualizarCampo(
                  seccionIndex,
                  campoIndex,
                  "requerido",
                  e.target.checked
                )
              }
            />{" "}
            Requerido
          </label>

          <label>
            <input
              type="checkbox"
              checked={campo.super_requerido ?? false}
              onChange={(e) =>
                actualizarCampo(
                  seccionIndex,
                  campoIndex,
                  "super_requerido",
                  e.target.checked
                )
              }
            />{" "}
            Super requerido
          </label>

          <label>
            <input
              type="checkbox"
              checked={campo.imprimible ?? true}
              onChange={(e) =>
                actualizarCampo(
                  seccionIndex,
                  campoIndex,
                  "imprimible",
                  e.target.checked
                )
              }
            />{" "}
            Imprimible
          </label>

          <label>
            <input
              type="checkbox"
              checked={campo.visible ?? true}
              onChange={(e) =>
                actualizarCampo(
                  seccionIndex,
                  campoIndex,
                  "visible",
                  e.target.checked
                )
              }
            />{" "}
            Visible
          </label>
        </div>

        <CampoPersonaEditor
          campo={campo}
          seccionIndex={seccionIndex}
          campoIndex={campoIndex}
          actualizarCampo={actualizarCampo}
        />

        <HtmlEditor
          campo={campo}
          seccionIndex={seccionIndex}
          campoIndex={campoIndex}
          actualizarCampo={actualizarCampo}
        />

        <OpcionesEditor
          campo={campo}
          seccionIndex={seccionIndex}
          campoIndex={campoIndex}
          agregarOpcion={agregarOpcion}
          eliminarOpcion={eliminarOpcion}
          actualizarOpcion={actualizarOpcion}
        />

        <BooleanEditor
          campo={campo}
          seccionIndex={seccionIndex}
          campoIndex={campoIndex}
          actualizarCampoInterno={actualizarCampoInterno}
        />

        <RepetibleEditor
          campo={campo}
          seccionIndex={seccionIndex}
          campoIndex={campoIndex}
          actualizarCampo={actualizarCampo}
        />

        <DependenciaEditor
          campo={campo}
          seccionIndex={seccionIndex}
          campoIndex={campoIndex}
          camposDisponibles={camposDisponibles}
          actualizarCampoInterno={actualizarCampoInterno}
        />
      </div>
    );
  }
);

CampoEditor.displayName = "CampoEditor";

const AccionesCamposSeleccionados = memo(
  ({
    cantidadSeleccionados,
    secciones,
    seccionDestinoIndex,
    setSeccionDestinoIndex,
    clonarCamposSeleccionados,
    moverCamposSeleccionados,
    limpiarSeleccionCampos,
  }) => {
    if (cantidadSeleccionados === 0) return null;

    return (
      <div style={styles.bulkBar}>
        <b>{cantidadSeleccionados} campo(s) seleccionado(s)</b>

        <button
          type="button"
          style={{ ...styles.button, ...styles.secondary, marginTop: 0 }}
          onClick={clonarCamposSeleccionados}
        >
          📄 Clonar seleccionados
        </button>

        <select
          style={{ ...styles.input, width: 260 }}
          value={seccionDestinoIndex}
          onChange={(e) => setSeccionDestinoIndex(e.target.value)}
        >
          <option value="">Mover a sección...</option>
          {secciones.map((sec, index) => (
            <option key={sec.id || sec._uid || index} value={index}>
              Sección {index + 1}: {sec.nombre || "Sin nombre"}
            </option>
          ))}
        </select>

        <button
          type="button"
          style={{ ...styles.button, ...styles.success, marginTop: 0 }}
          onClick={moverCamposSeleccionados}
        >
          ➡️ Mover seleccionados
        </button>

        <button
          type="button"
          style={{ ...styles.button, ...styles.danger, marginTop: 0 }}
          onClick={limpiarSeleccionCampos}
        >
          Limpiar selección
        </button>
      </div>
    );
  }
);

AccionesCamposSeleccionados.displayName = "AccionesCamposSeleccionados";

const SeccionEditor = memo(
  ({
    sec,
    seccionIndex,
    totalSecciones,
    actualizarSeccion,
    agregarCampo,
    moverSeccion,
    eliminarSeccion,
    actualizarCampo,
    actualizarCampoInterno,
    moverCampo,
    clonarCampo,
    eliminarCampo,
    agregarOpcion,
    eliminarOpcion,
    actualizarOpcion,
    camposSeleccionados,
    toggleCampoSeleccionado,
    seleccionarTodosSeccion,
    secciones,
  }) => {
    return (
      <div style={styles.card}>
        <div style={styles.sectionHeader}>
          <h3>Sección {seccionIndex + 1}</h3>

          <div>
            <button
              type="button"
              style={styles.button}
              disabled={seccionIndex === 0}
              onClick={() => moverSeccion(seccionIndex, -1)}
            >
              ⬆️
            </button>

            <button
              type="button"
              style={styles.button}
              disabled={seccionIndex === totalSecciones - 1}
              onClick={() => moverSeccion(seccionIndex, 1)}
            >
              ⬇️
            </button>

            <button
              type="button"
              style={{ ...styles.button, ...styles.danger }}
              onClick={() => eliminarSeccion(seccionIndex)}
            >
              ❌
            </button>
          </div>
        </div>

        <Input label="Nombre de sección">
          <input
            style={styles.input}
            value={sec.nombre || ""}
            onChange={(e) =>
              actualizarSeccion(seccionIndex, "nombre", e.target.value)
            }
          />
        </Input>

        <button
          type="button"
          style={{ ...styles.button, ...styles.success }}
          onClick={() => agregarCampo(seccionIndex)}
        >
          + Campo
        </button>

        <button
          type="button"
          style={{ ...styles.button, ...styles.secondary }}
          onClick={() => seleccionarTodosSeccion(seccionIndex)}
        >
          Seleccionar campos de esta sección
        </button>

        {(sec.campos || []).map((campo, campoIndex) => (
          <CampoEditor
            key={campo._uid || campo.id || campo.clave || campoIndex}
            campo={campo}
            seccionIndex={seccionIndex}
            campoIndex={campoIndex}
            totalCampos={(sec.campos || []).length}
            actualizarCampo={actualizarCampo}
            actualizarCampoInterno={actualizarCampoInterno}
            moverCampo={moverCampo}
            clonarCampo={clonarCampo}
            eliminarCampo={eliminarCampo}
            agregarOpcion={agregarOpcion}
            eliminarOpcion={eliminarOpcion}
            actualizarOpcion={actualizarOpcion}
            seleccionado={Boolean((camposSeleccionados || {})[campo._uid])}
            toggleCampoSeleccionado={toggleCampoSeleccionado}
            camposDisponibles={(secciones || []).flatMap((seccion) =>
              (seccion.campos || [])
                .filter((item) => item._uid !== campo._uid)
                .map((item) => ({
                  ...item,
                  seccionNombre: seccion.nombre || "Sección sin nombre",
                }))
            )}
          />
        ))}
      </div>
    );
  }
);

SeccionEditor.displayName = "SeccionEditor";

const FormBuilder = ({ formularioId }) => {
  const BASE_URL = process.env.REACT_APP_BASEURL;

  const [formulario, setFormulario] = useState({
    nombre: "",
    descripcion: "",
    escuela: "",
    sede: "",
    fechaInicio: "",
    fechaFinalizacion: "",
    tipo: "Cuestionario",
    restricciones: "",
    impresion: "Declaracion Jurada",
    turnos: "",
    activo: true,
    restringeFormulario: false,
  });

  const [secciones, setSecciones] = useState([]);
  const [camposSeleccionados, setCamposSeleccionados] = useState({});
  const [seccionDestinoIndex, setSeccionDestinoIndex] = useState("");

  const cargarFormulario = useCallback(async () => {
    const res = await axios.post(
      `${BASE_URL}ingresos/formularios/${formularioId}`
    );

    setFormulario({
      nombre: res.data.nombre || "",
      descripcion: res.data.descripcion || "",
      escuela: res.data.escuela || "",
      sede: res.data.sede || "",
      fechaInicio: normalizarFechaInput(res.data.fechaInicio),
      fechaFinalizacion: normalizarFechaInput(res.data.fechaFinalizacion),
      tipo: res.data.tipo || "Cuestionario",
      restricciones: res.data.restricciones || "",
      impresion: res.data.impresion || "Declaracion Jurada",
      turnos: res.data.turnos || "",
      activo: normalizarBool(res.data.activo ?? true),
      restringeFormulario: normalizarBool(res.data.restringeFormulario),
    });

    setSecciones(
      (res.data.Seccions || []).map((sec) => ({
        ...sec,
        _uid: sec.id ? `sec_${sec.id}` : generarIdTemp(),
        campos: (sec.Campos || []).map((c) => ({
          ...c,
          _uid: c.id ? `campo_${c.id}` : generarIdTemp(),
          requerido: normalizarBool(c.requerido),
          super_requerido: normalizarBool(c.super_requerido),
          valor_obligatorio: c.valor_obligatorio || "",
          imprimible: c.imprimible === undefined ? true : normalizarBool(c.imprimible),
          visible: c.visible === undefined ? true : normalizarBool(c.visible),
          origen: c.origen || "formulario",
          herencia:
            c.origen === "persona"
              ? c.herencia || "readonly"
              : "readonly",
          opciones: c.CampoOpcions || c.opciones || [],
          opciones_boolean: c.opciones_boolean || {
            no: "No",
            si: "Sí",
          },
          valor_dependencia: c.valor_dependencia
            ? JSON.parse(c.valor_dependencia)
            : { tipo: "igual", valores: [] },
          repetibles: c.repetibles || [],
        })),
      }))
    );
  }, [BASE_URL, formularioId]);

  useEffect(() => {
    if (formularioId) cargarFormulario();
  }, [formularioId, cargarFormulario]);

  const actualizarFormulario = useCallback((campo, valor) => {
    setFormulario((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  }, []);

  const actualizarSeccion = useCallback((seccionIndex, campo, valor) => {
    setSecciones((prev) =>
      prev.map((sec, i) =>
        i !== seccionIndex
          ? sec
          : {
              ...sec,
              [campo]: valor,
            }
      )
    );
  }, []);

  const actualizarCampo = useCallback(
    (seccionIndex, campoIndex, campo, valor) => {
      setSecciones((prev) =>
        prev.map((sec, i) => {
          if (i !== seccionIndex) return sec;

          return {
            ...sec,
            campos: (sec.campos || []).map((item, j) =>
              j !== campoIndex
                ? item
                : {
                    ...item,
                    [campo]: valor,
                  }
            ),
          };
        })
      );
    },
    []
  );

  const actualizarCampoInterno = useCallback(
    (seccionIndex, campoIndex, cambios) => {
      setSecciones((prev) =>
        prev.map((sec, i) => {
          if (i !== seccionIndex) return sec;

          return {
            ...sec,
            campos: (sec.campos || []).map((item, j) =>
              j !== campoIndex
                ? item
                : {
                    ...item,
                    ...cambios,
                  }
            ),
          };
        })
      );
    },
    []
  );

  const actualizarOpcion = useCallback(
    (seccionIndex, campoIndex, opcionIndex, campoOpcion, valor) => {
      setSecciones((prev) =>
        prev.map((sec, i) => {
          if (i !== seccionIndex) return sec;

          return {
            ...sec,
            campos: (sec.campos || []).map((campo, j) => {
              if (j !== campoIndex) return campo;

              return {
                ...campo,
                opciones: (campo.opciones || []).map((op, k) =>
                  k !== opcionIndex
                    ? op
                    : {
                        ...op,
                        [campoOpcion]: valor,
                      }
                ),
              };
            }),
          };
        })
      );
    },
    []
  );

  const moverSeccion = useCallback((index, direccion) => {
    setSecciones((prev) => {
      const nuevas = [...prev];
      const nuevoIndex = index + direccion;

      if (nuevoIndex < 0 || nuevoIndex >= nuevas.length) return prev;

      [nuevas[index], nuevas[nuevoIndex]] = [
        nuevas[nuevoIndex],
        nuevas[index],
      ];

      return nuevas.map((sec, i) => ({
        ...sec,
        orden: i,
      }));
    });
  }, []);

  const moverCampo = useCallback((seccionIndex, campoIndex, direccion) => {
    setSecciones((prev) =>
      prev.map((sec, i) => {
        if (i !== seccionIndex) return sec;

        const campos = [...(sec.campos || [])];
        const nuevoIndex = direccion === "up" ? campoIndex - 1 : campoIndex + 1;

        if (nuevoIndex < 0 || nuevoIndex >= campos.length) return sec;

        [campos[campoIndex], campos[nuevoIndex]] = [
          campos[nuevoIndex],
          campos[campoIndex],
        ];

        return {
          ...sec,
          campos: campos.map((campo, index) => ({
            ...campo,
            orden: index,
          })),
        };
      })
    );
  }, []);

  const agregarSeccion = useCallback(() => {
    setSecciones((prev) => [
      ...prev,
      {
        id: generarIdTemp(),
        _uid: generarIdTemp(),
        nombre: "",
        orden: prev.length,
        campos: [],
      },
    ]);
  }, []);

  const agregarCampo = useCallback((seccionIndex) => {
    setSecciones((prev) =>
      prev.map((sec, i) => {
        if (i !== seccionIndex) return sec;

        const camposActuales = sec.campos || [];

        return {
          ...sec,
          campos: [
            ...camposActuales,
            {
              id: generarIdTemp(),
              _uid: generarIdTemp(),
              label: "",
              clave: `campo_${Date.now()}`,
              tipo: "text",
              contenido_html: "",
              requerido: false,
              super_requerido: false,
              valor_obligatorio: "",
              imprimible: true,
              visible: true,
              origen: "formulario",
              herencia: "readonly",
              campo_db: "",
              opciones: [],
              modelo: "",
              opciones_boolean: {
                si: "Sí",
                no: "No",
              },
              tipo_relacion: "Otro",
              valor_dependencia: {
                tipo: "igual",
                valores: [],
              },
              orden: camposActuales.length,
            },
          ],
        };
      })
    );
  }, []);

  const clonarCampo = useCallback((seccionIndex, campoIndex) => {
    setSecciones((prev) =>
      prev.map((sec, i) => {
        if (i !== seccionIndex) return sec;

        const campos = [...(sec.campos || [])];
        const copia = crearCampoClonado(campos[campoIndex]);

        campos.splice(campoIndex + 1, 0, copia);

        return {
          ...sec,
          campos: reordenarCampos(campos),
        };
      })
    );
  }, []);

  const agregarOpcion = useCallback((seccionIndex, campoIndex) => {
    setSecciones((prev) =>
      prev.map((sec, i) => {
        if (i !== seccionIndex) return sec;

        return {
          ...sec,
          campos: (sec.campos || []).map((campo, j) => {
            if (j !== campoIndex) return campo;

            return {
              ...campo,
              opciones: [
                ...(campo.opciones || []),
                {
                  id: generarIdTemp(),
                  label: "",
                  valor: "",
                },
              ],
            };
          }),
        };
      })
    );
  }, []);

  const eliminarOpcion = useCallback((seccionIndex, campoIndex, opcionIndex) => {
    setSecciones((prev) =>
      prev.map((sec, i) => {
        if (i !== seccionIndex) return sec;

        return {
          ...sec,
          campos: (sec.campos || []).map((campo, j) => {
            if (j !== campoIndex) return campo;

            return {
              ...campo,
              opciones: (campo.opciones || []).filter(
                (_, k) => k !== opcionIndex
              ),
            };
          }),
        };
      })
    );
  }, []);

  const eliminarSeccion = useCallback(
    async (seccionIndex) => {
      const sec = secciones[seccionIndex];

      const result = await Swal.fire({
        title: "¿Eliminar sección?",
        text: "Esta acción no se puede deshacer.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (!result.isConfirmed) return;

      if (sec?.id && !String(sec.id).startsWith("tmp_")) {
        await axios.delete(`${BASE_URL}ingresos/secciones/${sec.id}`);
      }

      setSecciones((prev) => prev.filter((_, index) => index !== seccionIndex));
    },
    [BASE_URL, secciones]
  );

  const eliminarCampo = useCallback(
    async (seccionIndex, campoIndex) => {
      const campo = secciones[seccionIndex]?.campos?.[campoIndex];

      const result = await Swal.fire({
        title: "¿Eliminar campo?",
        text: campo?.label || "Campo sin nombre",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (!result.isConfirmed) return;

      if (campo?.id && !String(campo.id).startsWith("tmp_")) {
        await axios.delete(`${BASE_URL}ingresos/campos/${campo.id}`);
      }

      setSecciones((prev) =>
        prev.map((sec, i) => {
          if (i !== seccionIndex) return sec;

          return {
            ...sec,
            campos: (sec.campos || [])
              .filter((_, j) => j !== campoIndex)
              .map((item, orden) => ({
                ...item,
                orden,
              })),
          };
        })
      );
    },
    [BASE_URL, secciones]
  );


  const cantidadSeleccionados = useMemo(
    () => Object.keys(camposSeleccionados || {}).length,
    [camposSeleccionados]
  );

  const limpiarSeleccionCampos = useCallback(() => {
    setCamposSeleccionados({});
  }, []);

  const toggleCampoSeleccionado = useCallback((uid) => {
    if (!uid) return;

    setCamposSeleccionados((prev) => {
      const nuevo = { ...prev };

      if (nuevo[uid]) {
        delete nuevo[uid];
      } else {
        nuevo[uid] = true;
      }

      return nuevo;
    });
  }, []);

  const seleccionarTodosSeccion = useCallback((seccionIndex) => {
    setCamposSeleccionados((prev) => {
      const campos = secciones[seccionIndex]?.campos || [];
      const nuevo = { ...prev };
      const todosSeleccionados = campos.length > 0 && campos.every((campo) => nuevo[campo._uid]);

      campos.forEach((campo) => {
        if (!campo._uid) return;

        if (todosSeleccionados) {
          delete nuevo[campo._uid];
        } else {
          nuevo[campo._uid] = true;
        }
      });

      return nuevo;
    });
  }, [secciones]);

  const clonarCamposSeleccionados = useCallback(() => {
    if (cantidadSeleccionados === 0) return;

    setSecciones((prev) =>
      prev.map((sec) => {
        const camposConClones = [];

        (sec.campos || []).forEach((campo) => {
          camposConClones.push(campo);

          if ((camposSeleccionados || {})[campo._uid]) {
            camposConClones.push(crearCampoClonado(campo));
          }
        });

        return {
          ...sec,
          campos: reordenarCampos(camposConClones),
        };
      })
    );

    setCamposSeleccionados({});
  }, [camposSeleccionados, cantidadSeleccionados]);

  const moverCamposSeleccionados = useCallback(() => {
    if (cantidadSeleccionados === 0) return;

    const destinoIndex = Number(seccionDestinoIndex);

    if (Number.isNaN(destinoIndex) || !secciones[destinoIndex]) {
      Swal.fire({
        icon: "warning",
        title: "Seleccioná una sección destino",
        text: "Primero elegí a qué sección querés mover los campos.",
      });
      return;
    }

    setSecciones((prev) => {
      const camposMovidos = [];

      const seccionesSinSeleccionados = prev.map((sec) => {
        const camposRestantes = [];

        (sec.campos || []).forEach((campo) => {
          if ((camposSeleccionados || {})[campo._uid]) {
            camposMovidos.push(campo);
          } else {
            camposRestantes.push(campo);
          }
        });

        return {
          ...sec,
          campos: reordenarCampos(camposRestantes),
        };
      });

      if (camposMovidos.length === 0) return prev;

      return seccionesSinSeleccionados.map((sec, index) => {
        if (index !== destinoIndex) return sec;

        return {
          ...sec,
          campos: reordenarCampos([...(sec.campos || []), ...camposMovidos]),
        };
      });
    });

    setCamposSeleccionados({});
    setSeccionDestinoIndex("");
  }, [camposSeleccionados, cantidadSeleccionados, seccionDestinoIndex, secciones]);

  const descargarVistaPreviaPdf = useCallback(async () => {
    try {
      Swal.fire({
        title: "Generando PDF...",
        text: "Preparando la vista previa completa del formulario",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const documento = (
        <FormularioPdfDocument
          formulario={formulario}
          secciones={secciones}
        />
      );

      const blob = await pdf(documento).toBlob();
      const url = URL.createObjectURL(blob);
      const enlace = document.createElement("a");

      enlace.href = url;
      enlace.download = `${nombreArchivoSeguro(formulario.nombre)}_vista_previa.pdf`;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();

      setTimeout(() => URL.revokeObjectURL(url), 1000);

      Swal.fire({
        icon: "success",
        title: "PDF generado ✅",
        text: "Se descargó la vista previa con todos los campos actuales.",
      });
    } catch (error) {
      console.error("Error generando vista previa PDF:", error);

      Swal.fire({
        icon: "error",
        title: "No se pudo generar el PDF",
        text: error.message || "Ocurrió un error al crear la vista previa.",
      });
    }
  }, [formulario, secciones]);

  const guardarTodo = useCallback(async () => {
    try {
      const mapaIds = {};
      let formId = formularioId;

      Swal.fire({
        title: "Guardando formulario...",
        text: "Por favor espera...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const payloadFormulario = {
        ...formulario,
        fechaInicio: formulario.fechaInicio || null,
        fechaFinalizacion: formulario.fechaFinalizacion || null,
        activo: formulario.activo ? 1 : 0,
        restringeFormulario: formulario.restringeFormulario ? 1 : 0,
      };

      if (formularioId) {
        await axios.put(
          `${BASE_URL}ingresos/formularios/${formularioId}`,
          payloadFormulario
        );

        Swal.update({
          text: "Formulario actualizado ✅",
        });
      } else {
        const res = await axios.post(
          `${BASE_URL}ingresos/formularios`,
          payloadFormulario
        );

        formId = res.data.id;

        Swal.update({
          text: "Formulario creado ✅",
        });
      }

      for (const [i, sec] of secciones.entries()) {
        let secId = sec.id;

        Swal.update({
          text: `Guardando sección: ${sec.nombre || `Sección ${i + 1}`}...`,
        });

        if (sec.id && !String(sec.id).startsWith("tmp_")) {
          await axios.put(`${BASE_URL}ingresos/secciones/${sec.id}`, {
            nombre: sec.nombre,
            orden: i,
          });
        } else {
          const res = await axios.post(`${BASE_URL}ingresos/secciones`, {
            formulario_id: formId,
            nombre: sec.nombre,
            orden: i,
          });

          secId = res.data.id;

          if (sec.id && String(sec.id).startsWith("tmp_")) {
            mapaIds[sec.id] = secId;
          }
        }

        for (const [j, campo] of (sec.campos || []).entries()) {
          let campoId = campo.id;

          Swal.update({
            text: `Guardando campo: ${campo.label || `Campo ${j + 1}`}...`,
          });

          const dependeReal =
            mapaIds[campo.depende_de] ||
            (campo.depende_de && !String(campo.depende_de).startsWith("tmp_")
              ? campo.depende_de
              : null);

          const payload = {
            seccion_id: secId,
            label: campo.label || `Campo ${j + 1}`,
            clave: campo.clave,
            tipo: campo.tipo,
            requerido: campo.requerido ?? false,
            super_requerido: campo.super_requerido ?? false,
            valor_obligatorio: campo.valor_obligatorio || null,
            orden: j,
            origen: campo.origen,
            herencia:
              campo.origen === "persona"
                ? normalizarHerencia(campo)
                : "readonly",
            campo_db: campo.origen === "persona" ? campo.campo_db : null,
            modelo: campo.tipo === "repetible" ? campo.modelo : null,
            imprimible: campo.imprimible ?? true,
            visible: campo.visible ?? true,
            editable: true,
            contenido_html:
              campo.tipo === "html_static" ? campo.contenido_html : null,
            depende_de: dependeReal,
            valor_dependencia: campo.valor_dependencia
              ? JSON.stringify(campo.valor_dependencia)
              : null,
          };

          if (
            campo.tipo === "repetible" &&
            campo.modelo === "persona_hijos"
          ) {
            payload.tipo_relacion = campo.tipo_relacion;
          }

          if (campo.id && !String(campo.id).startsWith("tmp_")) {
            await axios.put(
              `${BASE_URL}ingresos/campos/${campo.id}`,
              payload
            );
          } else {
            const res = await axios.post(
              `${BASE_URL}ingresos/campos`,
              payload
            );

            campoId = res.data.id;

            if (campo.id && String(campo.id).startsWith("tmp_")) {
              mapaIds[campo.id] = campoId;
            }
          }

          if (
            campo.tipo === "select" ||
            campo.tipo === "multi-select" ||
            campo.tipo === "boolean" ||
            campo.tipo === "aceptacion_obligatoria"
          ) {
            if (campo.id && !String(campo.id).startsWith("tmp_")) {
              await axios.delete(
                `${BASE_URL}ingresos/campo-opciones/${campoId}`
              );
            }

            let opcionesFinales = campo.opciones || [];

            if (campo.tipo === "boolean") {
              opcionesFinales = [
                {
                  label: campo.opciones_boolean?.no || "No",
                  valor: "false",
                },
                {
                  label: campo.opciones_boolean?.si || "Sí",
                  valor: "true",
                },
              ];
            }

            if (opcionesFinales.length > 0) {
              await axios.post(`${BASE_URL}ingresos/campo-opciones`, {
                campo_id: campoId,
                opciones: opcionesFinales,
              });
            }
          }
        }
      }

      Swal.fire({
        icon: "success",
        title: "Guardado completo ✅",
        text: "Formulario guardado correctamente",
      });
    } catch (error) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Error guardando",
        text:
          error.response?.data?.message ||
          error.message ||
          "Hubo un error al guardar",
      });
    }
  }, [BASE_URL, formulario, formularioId, secciones]);

  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Form Builder PRO 🚀</h2>

      <DatosFormulario
        formulario={formulario}
        actualizarFormulario={actualizarFormulario}
      />

      <button type="button" style={styles.button} onClick={agregarSeccion}>
        + Sección
      </button>

      <button
        type="button"
        style={{ ...styles.button, ...styles.preview }}
        onClick={descargarVistaPreviaPdf}
      >
        📄 Descargar vista previa PDF
      </button>

      <AccionesCamposSeleccionados
        cantidadSeleccionados={cantidadSeleccionados}
        secciones={secciones}
        seccionDestinoIndex={seccionDestinoIndex}
        setSeccionDestinoIndex={setSeccionDestinoIndex}
        clonarCamposSeleccionados={clonarCamposSeleccionados}
        moverCamposSeleccionados={moverCamposSeleccionados}
        limpiarSeleccionCampos={limpiarSeleccionCampos}
      />

      {secciones.map((sec, seccionIndex) => (
        <SeccionEditor
          key={sec._uid || sec.id || seccionIndex}
          sec={sec}
          seccionIndex={seccionIndex}
          totalSecciones={secciones.length}
          actualizarSeccion={actualizarSeccion}
          agregarCampo={agregarCampo}
          moverSeccion={moverSeccion}
          eliminarSeccion={eliminarSeccion}
          actualizarCampo={actualizarCampo}
          actualizarCampoInterno={actualizarCampoInterno}
          moverCampo={moverCampo}
          clonarCampo={clonarCampo}
          eliminarCampo={eliminarCampo}
          agregarOpcion={agregarOpcion}
          eliminarOpcion={eliminarOpcion}
          actualizarOpcion={actualizarOpcion}
          camposSeleccionados={camposSeleccionados}
          toggleCampoSeleccionado={toggleCampoSeleccionado}
          seleccionarTodosSeccion={seleccionarTodosSeccion}
          secciones={secciones}
        />
      ))}

      <hr />

      <button
        type="button"
        style={{ ...styles.button, ...styles.success, fontSize: 16 }}
        onClick={guardarTodo}
      >
        💾 Guardar todo
      </button>
    </div>
  );
};

export default FormBuilder;
